// Mock data for Snapcom landing page

export const mockWaitlistSubmission = async (data) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // Store in localStorage for demo purposes
  const submissions = JSON.parse(localStorage.getItem('waitlist') || '[]');
  const newSubmission = {
    id: `mock_${Date.now()}`,
    ...data,
    createdAt: new Date().toISOString(),
    status: 'pending'
  };
  
  submissions.push(newSubmission);
  localStorage.setItem('waitlist', JSON.stringify(submissions));
  
  return {
    success: true,
    message: 'Successfully joined the waitlist!',
    data: newSubmission
  };
};

export const features = [
  {
    id: 1,
    title: 'Instant Product Recognition',
    description: 'Upload or capture a product photo and our AI instantly identifies it, fetching accurate details from multiple marketplaces.',
    icon: 'Camera'
  },
  {
    id: 2,
    title: 'Smart Data Aggregation',
    description: 'Automatically gather product descriptions, specifications, high-res images, pricing, and reviews from Amazon, Flipkart, and Shopify.',
    icon: 'Database'
  },
  {
    id: 3,
    title: 'One-Click Publishing',
    description: 'Edit and customize your product listing, then publish directly to multiple marketplaces with a single click.',
    icon: 'Zap'
  },
  {
    id: 4,
    title: 'Competitive Pricing Insights',
    description: 'View real-time price comparisons across platforms to make informed pricing decisions and maximize profits.',
    icon: 'TrendingUp'
  },
  {
    id: 5,
    title: 'Review & Rating Analysis',
    description: 'Access aggregated customer reviews and ratings to understand product demand and quality before listing.',
    icon: 'Star'
  },
  {
    id: 6,
    title: 'Bulk Operations',
    description: 'Process multiple products simultaneously to build your catalogue faster and scale your business efficiently.',
    icon: 'Layers'
  }
];

export const platforms = [
  {
    id: 1,
    name: 'Amazon',
    description: 'Seamless integration with Amazon Seller Central',
    logo: '/amazon-logo.png',
    color: '#FF9900'
  },
  {
    id: 2,
    name: 'Flipkart',
    description: 'Connect with Flipkart Seller Hub',
    logo: '/flipkart-logo.png',
    color: '#2874F0'
  },
  {
    id: 3,
    name: 'Shopify',
    description: 'Sync with your Shopify store',
    logo: '/shopify-logo.png',
    color: '#96BF48'
  }
];

export const howItWorks = [
  {
    step: 1,
    title: 'Upload Photo',
    description: 'Simply capture or upload a product image using your mobile device',
    icon: 'Upload'
  },
  {
    step: 2,
    title: 'Review & Edit',
    description: 'Our AI fetches product details. Review pricing, descriptions, and specifications',
    icon: 'Edit'
  },
  {
    step: 3,
    title: 'Publish Instantly',
    description: 'Click publish to add the product to your selected marketplace catalogues',
    icon: 'Rocket'
  }
];

export const pricingPlans = [
  {
    id: 1,
    name: 'Starter Pack',
    price: '₹0',
    originalPrice: '₹199',
    bookingAmount: '₹90',
    period: 'forever',
    description: 'Perfect for getting started',
    features: [
      '20 product scans per month',
      '5 catalogue uploads per month',
      'Single marketplace integration',
      'Basic product data',
      'Email support'
    ],
    cta: 'Book Early Access',
    highlighted: false
  },
  {
    id: 2,
    name: 'Pro',
    price: '₹999',
    originalPrice: '₹1,999',
    bookingAmount: '₹90',
    period: 'per month',
    description: 'For growing sellers who need more power',
    features: [
      '200 product scans/month',
      '200 catalogue uploads',
      'Advanced data fetch with images',
      'Publish to Amazon, Flipkart, Shopify',
      'Price comparison across platforms',
      'Priority support'
    ],
    cta: 'Book Early Access',
    highlighted: true
  },
  {
    id: 3,
    name: 'Business',
    price: '₹2,499',
    originalPrice: '₹4,999',
    bookingAmount: '₹90',
    period: 'per month',
    description: 'For teams that need automation',
    features: [
      'Unlimited product scans',
      'Up to 5 team members',
      'Autopilot pricing mode',
      'Auto-undercut lowest price by %',
      'Review & sentiment analysis',
      'Bulk upload via CSV',
      'Dedicated account manager'
    ],
    cta: 'Book Early Access',
    highlighted: false
  },
  {
    id: 4,
    name: 'Enterprise',
    price: 'Custom',
    period: 'contact us',
    description: 'For large operations with custom needs',
    features: [
      'Everything in Business',
      'Unlimited team members',
      'Custom API access',
      'White-label solution',
      'Custom integrations',
      'SLA guarantee',
      '24/7 priority support'
    ],
    cta: 'Contact Sales',
    highlighted: false
  }
];

export const faqs = [
  {
    id: 1,
    question: 'How does Snapcom identify products?',
    answer: 'Snapcom uses advanced image recognition AI combined with marketplace APIs to accurately identify products. Simply upload a photo, and our system searches across Amazon, Flipkart, and Shopify to find matching products and retrieve their details.'
  },
  {
    id: 2,
    question: 'Which marketplaces are supported?',
    answer: 'Currently, Snapcom supports Amazon, Flipkart, and Shopify. We are constantly working to add more platforms based on user demand. If you need a specific marketplace integration, please let us know!'
  },
  {
    id: 3,
    question: 'Is my seller account data secure?',
    answer: 'Absolutely! We use bank-level encryption to protect your marketplace credentials and never store sensitive information. All connections to marketplaces use OAuth 2.0 authentication, and your data is encrypted both in transit and at rest.'
  },
  {
    id: 4,
    question: 'Can I edit product details before publishing?',
    answer: 'Yes! After Snapcom fetches product information, you can review and edit all fields including title, description, pricing, specifications, and images before publishing to your marketplace catalogues.'
  },
  {
    id: 5,
    question: 'What happens after the free trial?',
    answer: 'Your Starter Pack includes 20 scans and 5 uploads per month forever - no credit card required. When you are ready to scale, upgrade to Business for unlimited scans and uploads. You can upgrade, downgrade, or cancel anytime.'
  },
  {
    id: 6,
    question: 'Do I need technical knowledge to use Snapcom?',
    answer: 'Not at all! Snapcom is designed for sellers of all technical levels. If you can take a photo with your phone, you can use Snapcom. Our intuitive interface guides you through every step.'
  },
  {
    id: 7,
    question: 'How accurate is the pricing data?',
    answer: 'We fetch real-time pricing data directly from marketplace APIs, ensuring maximum accuracy. Prices are updated every few hours to reflect current market conditions. However, always verify final pricing before publishing.'
  },
  {
    id: 8,
    question: 'Can I use Snapcom on mobile devices?',
    answer: 'Yes! Snapcom is a mobile-first application designed for both Android and iOS. You can capture products on the go and manage your catalogue from anywhere.'
  }
];

export const stats = [
  { label: 'Active Sellers', value: '10K+' },
  { label: 'Products Listed', value: '500K+' },
  { label: 'Time Saved', value: '1M+ hrs' },
  { label: 'Marketplaces', value: '3' }
];